/**
 * 資料層：所有資料透過 Go 後端的 /api/* 存取，瀏覽器不碰資料庫、也拿不到任何金鑰。
 *
 * 讀取：useApi() —— 迷你 SWR：useSyncExternalStore + 每 4 秒輪詢 + 視窗聚焦時刷新，
 * 跨裝置也能近即時同步。
 * 寫入：async 函式打 API，成功後讓所有訂閱中的資料重新抓一次。
 */

import { useCallback, useSyncExternalStore } from "react";
import type { Attendee, Flight } from "./types";
import type {
  BoardFlight,
  FlightView,
  InviteView,
  ManifestView,
  PassView,
  PublicAttendee,
  ChatMessage,
  ChatView,
  ScanResult,
  SignupReason,
  SignupResult,
} from "./views";

export { useHydrated } from "./client";
export type { ScanResult } from "./views";

// ── 後端位址 ───────────────────────────────────────────────

/**
 * 空字串 = 同源：nginx 同時服務這個 SPA 與 /api，瀏覽器看到同一個 origin。
 * 這是建議的部署方式，沒有 CORS、cookie 用 SameSite=Lax 就夠。
 *
 * 設成 https://api.example.com = 分離部署（前端在 Cloudflare Pages）。
 * 這時後端也必須把同一個前端網址列進 APP_ORIGINS，否則瀏覽器會擋掉請求。
 */
const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");

const apiUrl = (path: string) => API_BASE + path;

/**
 * 所有請求都帶上 cookie。同源時本來就會帶，
 * 分離部署時沒有 include 的話 session cookie 根本不會送出去，後台會一直是未登入。
 */
const CREDENTIALS: RequestCredentials = "include";

// ── 迷你 SWR 快取 ───────────────────────────────────────────

interface Entry {
  key: string;
  /** undefined = 載入中；null = 404；其他 = 資料 */
  data: unknown;
  raw: string;
  listeners: Set<() => void>;
  timer: ReturnType<typeof setInterval> | null;
  inflight: Promise<void> | null;
  pollMs: number;
}

const entries = new Map<string, Entry>();

function getEntry(key: string, pollMs: number): Entry {
  let e = entries.get(key);
  if (!e) {
    e = { key, data: undefined, raw: "", listeners: new Set(), timer: null, inflight: null, pollMs };
    entries.set(key, e);
  }
  return e;
}

function refetch(e: Entry): Promise<void> {
  if (e.inflight) return e.inflight;
  e.inflight = (async () => {
    try {
      const res = await fetch(apiUrl(e.key), { cache: "no-store", credentials: CREDENTIALS });
      if (res.status === 404) {
        if (e.data !== null) {
          e.data = null;
          e.raw = "";
          e.listeners.forEach((l) => l());
        }
        return;
      }
      if (!res.ok) return; // 401 / 5xx：保留現況，下次輪詢再試
      const raw = await res.text();
      if (raw !== e.raw) {
        e.raw = raw;
        e.data = JSON.parse(raw);
        e.listeners.forEach((l) => l());
      }
    } catch {
      // 網路錯誤：保留現況
    } finally {
      e.inflight = null;
    }
  })();
  return e.inflight;
}

function subscribeEntry(e: Entry, l: () => void): () => void {
  e.listeners.add(l);
  if (e.listeners.size === 1) {
    void refetch(e);
    e.timer = setInterval(() => void refetch(e), e.pollMs);
  }
  return () => {
    e.listeners.delete(l);
    if (e.listeners.size === 0 && e.timer) {
      clearInterval(e.timer);
      e.timer = null;
    }
  };
}

/** 任何寫入成功後：讓所有畫面上訂閱中的資料立即刷新 */
function invalidateAll() {
  for (const e of entries.values()) {
    if (e.listeners.size > 0) void refetch(e);
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("focus", invalidateAll);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") invalidateAll();
  });
}

function useApi<T>(path: string | null, pollMs = 4000): T | null | undefined {
  // subscribe/getSnapshot 的參照要穩定，否則每次 render 都會退訂重訂（重打 API、重設輪詢）
  const subscribe = useCallback(
    (l: () => void) => (path ? subscribeEntry(getEntry(path, pollMs), l) : () => {}),
    [path, pollMs],
  );
  const getSnapshot = useCallback(
    () => (path ? (entries.get(path)?.data as T | null | undefined) : undefined),
    [path],
  );
  return useSyncExternalStore(subscribe, getSnapshot, () => undefined);
}

async function send(
  path: string,
  method: string,
  body?: unknown,
): Promise<{ status: number; json: unknown }> {
  let status = 0;
  let json: unknown = null;
  try {
    const res = await fetch(apiUrl(path), {
      method,
      credentials: CREDENTIALS,
      headers: body === undefined ? undefined : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    status = res.status;
    try {
      json = await res.json();
    } catch {}
  } catch {}
  invalidateAll();
  return { status, json };
}

// ── 讀取 hooks ─────────────────────────────────────────────

/** 航班列表 + 人數統計（首頁、後台首頁） */
export function useBoard(): BoardFlight[] | undefined {
  const data = useApi<{ flights: BoardFlight[] }>("/api/flights", 8000);
  return data ? data.flights : undefined;
}

/** 單一活動的公開視角（活動頁、報名頁、選位頁） */
export function useFlightView(code: string | null): FlightView | null | undefined {
  return useApi<FlightView>(code ? `/api/flights/${encodeURIComponent(code)}` : null);
}

/** 登機證（token 即通行證，只有本人連結能看） */
export function usePassView(token: string | null): PassView | null | undefined {
  return useApi<PassView>(token ? `/api/pass/${encodeURIComponent(token)}` : null);
}

/** 後台乘客名單（需要 admin session） */
export function useManifest(code: string | null): ManifestView | null | undefined {
  return useApi<ManifestView>(code ? `/api/admin/flights/${encodeURIComponent(code)}` : null);
}

/**
 * 桌邊聊天。輪詢比其他資料快一點（3 秒）——
 * 聊天要有「即時感」，但還不到需要 WebSocket 的程度。
 */
export function useChat(token: string | null): ChatView | null | undefined {
  return useApi<ChatView>(token ? `/api/pass/${encodeURIComponent(token)}/chat` : null, 3000);
}

/** 主辦人的聊天室檢視（可刪文） */
export function useAdminChat(code: string | null): ChatView | null | undefined {
  return useApi<ChatView>(code ? `/api/admin/flights/${encodeURIComponent(code)}/chat` : null, 5000);
}

/** admin 登入狀態 */
export function useAdminSession(): boolean | undefined {
  const data = useApi<{ ok: boolean }>("/api/admin/session", 5 * 60_000);
  return data === undefined ? undefined : !!data?.ok;
}

// ── view 上的純函式（取代舊 selector；實作在 views.ts）─────

export { confirmedCountOf, inviteByName, standbyCountOf, takenSeatsOf } from "./views";

// ── 寫入（全部經過 server 驗證）────────────────────────────

export interface GroupSignup {
  flightCode: string;
  inviteId?: string;
  people: { name: string; industry?: string }[];
  note?: string;
  /** 與 people 一一對應；候補時傳空陣列 */
  seats: string[];
}

export async function signupGroup(input: GroupSignup): Promise<SignupResult> {
  const { status, json } = await send(
    `/api/flights/${encodeURIComponent(input.flightCode)}/signup`,
    "POST",
    { people: input.people, note: input.note, seats: input.seats, inviteId: input.inviteId },
  );
  const r = json as SignupResult | null;
  if (r && typeof r === "object" && "ok" in r) return r;
  return { ok: false, reason: status === 404 ? "flight-missing" : "seat-mismatch" };
}

export async function assignSeatByToken(
  token: string,
  seat: string,
): Promise<{ ok: boolean; reason?: SignupReason | "not-eligible" }> {
  const { json } = await send(`/api/pass/${encodeURIComponent(token)}/seat`, "POST", { seat });
  const r = json as { ok?: boolean; reason?: SignupReason | "not-eligible" } | null;
  return { ok: !!r?.ok, reason: r?.reason };
}

export async function adminLogin(code: string): Promise<boolean> {
  const { json } = await send("/api/admin/session", "POST", { code });
  return !!(json as { ok?: boolean } | null)?.ok;
}

export async function adminLogout(): Promise<void> {
  await send("/api/admin/session", "DELETE");
}

/** 一種桌型：count 桌，每桌 seats 位。餐廳常常不是統一桌型。 */
export interface TableGroup {
  count: number;
  seats: number;
  vip: boolean;
}

export interface NewFlight {
  code: string;
  title: string;
  departAt: string;
  endAt?: string;
  boardingMinutes: number;
  origin: string;
  venueName: string;
  venueAddress: string;
  gate: string;
  /** 桌型清單，桌號依序連號編下去 */
  tableGroups: TableGroup[];
  inviteOnly?: boolean;
  notes?: string;
}

export async function createFlight(
  input: NewFlight,
): Promise<{ ok: true; flight: Flight } | { ok: false; error: "code-exists" | "failed" }> {
  const { status, json } = await send("/api/admin/flights", "POST", input);
  const flight = (json as { flight?: Flight } | null)?.flight;
  if (flight) return { ok: true, flight };
  return { ok: false, error: status === 409 ? "code-exists" : "failed" };
}

export async function setFlightStatus(code: string, status: Flight["status"]): Promise<void> {
  await send(`/api/admin/flights/${encodeURIComponent(code)}`, "PATCH", { status });
}

/** 刪掉一場活動時，跟著一起消失的東西 */
export interface FlightDeletion {
  confirmed: number;
  standby: number;
  cancelled: number;
  invites: number;
  messages: number;
}

/**
 * 刪掉整場活動，連同它的報名、接龍名單與桌邊聊天。無法復原。
 *
 * confirm 要是主辦人親手打進去的活動代號 —— 後端會再比對一次。
 * 這不是多此一舉：後台首頁一排活動長得幾乎一樣，
 * 而點錯一行的代價是那場所有人的報名與登機證一起消失。
 */
export async function deleteFlight(
  code: string,
  confirm: string,
): Promise<
  | { ok: true; deleted: FlightDeletion }
  | { ok: false; error: "confirm-mismatch" | "not-found" | "failed" }
> {
  const { status, json } = await send(`/api/admin/flights/${encodeURIComponent(code)}`, "DELETE", {
    confirm,
  });
  if (status === 200) {
    return { ok: true, deleted: (json as { deleted: FlightDeletion }).deleted };
  }
  if (status === 400) return { ok: false, error: "confirm-mismatch" };
  if (status === 404) return { ok: false, error: "not-found" };
  return { ok: false, error: "failed" };
}

/**
 * 儲存場地平面配置。只送座標 —— 後端刻意不接受桌號 / 座位數的變更，
 * 因為改座位數會讓已經選好的位子失效。
 */
export async function saveLayout(
  code: string,
  positions: { label: string; x: number; y: number }[],
): Promise<boolean> {
  const { status } = await send(
    `/api/admin/flights/${encodeURIComponent(code)}/layout`,
    "PATCH",
    { positions },
  );
  return status === 200;
}

/** 桌號的長度上限（字元數），與後端 maxTableLabel 一致 */
export const MAX_TABLE_LABEL = 6;

/**
 * 改桌號（桌子顯示的名稱）。
 *
 * 座位代號 = 桌號 + 字母，所以後端會在同一個交易裡把坐在這桌的人
 * 一起換過去（2G → 主桌G），不會留下指向舊桌號的座位。
 * 桌號不能跟其他桌重複 —— 重複的話兩桌會產生同一組座位代號。
 */
export async function renameTable(
  code: string,
  label: string,
  newLabel: string,
): Promise<{ ok: true; label: string } | { ok: false; error: "duplicate" | "invalid" | "failed" }> {
  const { status, json } = await send(
    `/api/admin/flights/${encodeURIComponent(code)}/tables`,
    "PATCH",
    { label, newLabel },
  );
  if (status === 200) {
    const saved = (json as { label?: string } | null)?.label;
    return { ok: true, label: saved ?? newLabel };
  }
  if (status === 409) return { ok: false, error: "duplicate" };
  if (status === 400) return { ok: false, error: "invalid" };
  return { ok: false, error: "failed" };
}

/** 每桌座位數的範圍，與後端 minTableSeats / maxTableSeats 一致 */
export const MIN_TABLE_SEATS = 2;
export const MAX_TABLE_SEATS = 20;

/** 縮桌被擋下來時，坐在那些位子上的人 */
export interface SeatHolder {
  seat: string;
  name: string;
}

/**
 * 改一桌的座位數。
 *
 * 加座位永遠可以（既有座位代號一個都沒少）。
 * 減座位只有在「被砍掉的那幾個位子沒人坐」時才會過 —— 座位代號是
 * 「桌號 + 字母」，8 位縮成 6 位就是把 G、H 兩個位子拿掉，
 * 有人坐著的話後端會回 seat-occupied 並告訴你是誰。
 */
export async function resizeTable(
  code: string,
  label: string,
  seats: number,
): Promise<
  | { ok: true; seats: number }
  | { ok: false; error: "occupied"; occupied: SeatHolder[] }
  | { ok: false; error: "below-confirmed"; confirmed: number; capacity: number }
  | { ok: false; error: "failed" }
> {
  const { status, json } = await send(
    `/api/admin/flights/${encodeURIComponent(code)}/tables`,
    "PATCH",
    { label, seats },
  );
  if (status === 200) return { ok: true, seats };
  const body = json as {
    reason?: string;
    occupied?: SeatHolder[];
    confirmed?: number;
    capacity?: number;
  } | null;
  if (status === 409 && body?.reason === "seat-occupied") {
    return { ok: false, error: "occupied", occupied: body.occupied ?? [] };
  }
  if (status === 409 && body?.reason === "below-confirmed") {
    return {
      ok: false,
      error: "below-confirmed",
      confirmed: body.confirmed ?? 0,
      capacity: body.capacity ?? 0,
    };
  }
  return { ok: false, error: "failed" };
}

export async function setInviteOnly(code: string, inviteOnly: boolean): Promise<void> {
  await send(`/api/admin/flights/${encodeURIComponent(code)}`, "PATCH", { inviteOnly });
}

export async function importInvites(
  flightCode: string,
  entries: { name: string; quota: number }[],
): Promise<{ added: number; updated: number } | null> {
  const { json } = await send("/api/admin/invites", "POST", { flightCode, entries });
  const r = json as { added?: number; updated?: number } | null;
  return r && typeof r.added === "number" ? { added: r.added, updated: r.updated ?? 0 } : null;
}

export async function setInviteQuota(inviteId: string, quota: number): Promise<void> {
  await send(`/api/admin/invites/${encodeURIComponent(inviteId)}`, "PATCH", { quota });
}

export async function deleteInvite(inviteId: string): Promise<void> {
  await send(`/api/admin/invites/${encodeURIComponent(inviteId)}`, "DELETE");
}

async function attendeeOp(id: string, op: "cancel" | "promote" | "board" | "unboard") {
  await send(`/api/admin/attendees/${encodeURIComponent(id)}`, "PATCH", { op });
}

export async function cancelAttendee(id: string): Promise<void> {
  await attendeeOp(id, "cancel");
}

export async function promoteStandby(id: string): Promise<void> {
  await attendeeOp(id, "promote");
}

export async function setBoarded(id: string, boarded: boolean): Promise<void> {
  await attendeeOp(id, boarded ? "board" : "unboard");
}

/**
 * 主辦人代改乘客的姓名與產業（報名時打錯字、或用了別的稱呼）。
 *
 * 不影響已經發出的登機證：票號沒變，對方手上的連結照常有效，
 * 打開就會看到新的名字。接龍名額也不會退回 —— 那是報名當下就扣掉的。
 */
export async function editAttendee(
  id: string,
  name: string,
  industry: string,
): Promise<boolean> {
  const { status } = await send(`/api/admin/attendees/${encodeURIComponent(id)}`, "PATCH", {
    op: "edit",
    name,
    industry,
  });
  return status === 200;
}

export async function scanToken(flightCode: string, token: string): Promise<ScanResult> {
  const { json } = await send("/api/admin/scan", "POST", { flightCode, token });
  const r = json as ScanResult | null;
  return r && typeof r === "object" && "kind" in r ? r : { kind: "invalid" };
}

export async function postChat(
  token: string,
  body: string,
): Promise<{ ok: boolean; reason?: string }> {
  const { status, json } = await send(
    `/api/pass/${encodeURIComponent(token)}/chat`,
    "POST",
    { body },
  );
  if (status === 200) return { ok: true };
  return { ok: false, reason: (json as { reason?: string } | null)?.reason };
}

export async function deleteChatMessage(id: string): Promise<void> {
  await send(`/api/admin/chat/${encodeURIComponent(id)}`, "DELETE");
}

export type { Attendee, Flight };
export type {
  BoardFlight,
  ChatMessage,
  ChatView,
  FlightView,
  InviteView,
  ManifestView,
  PassView,
  PublicAttendee,
  SignupResult,
};
